import React, { useState, useEffect, useContext, useCallback, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";

// OpenLayers
import Geometry from "ol/geom/Geometry";
import Point from "ol/geom/Point";
import LineString from "ol/geom/LineString";
import Polygon from "ol/geom/Polygon";
import proj4 from "proj4";
import { register as OlRegister } from "ol/proj/proj4";
import { transform as olTransform } from "ol/proj";
import OlSourceVector from "ol/source/Vector";
import { DrawEvent } from "ol/interaction/Draw";
import Modify from "ol/interaction/Modify";
import { unByKey } from "ol/Observable";
import OlBaseLayer from "ol/layer/Base";
import OlFeature from "ol/Feature";
import OlCollection from "ol/Collection";
import * as OlExtent from "ol/extent";

// MUI
import DeleteOutline from "@mui/icons-material/DeleteOutline";

// Custom
import { Controls, ZoomControl, ScaleLineControl } from "@/components/Map/Controls";
import ButtonControl from "@/components/Map/Controls/Custom/Button/ButtonControl";
import Map from "@/components/Map/Map";
import { Layers, VectorLayer } from "@/components/Map/Layers";
import { Overlays } from "@/components/Map/Overlays";
import { Interactions, DefaultInteractions, DrawInteraction } from "@/components/Map/Interactions";
import {
  measuringStyle,
  selectedStrokeOnlyStyle,
  highlightedVectorCustom,
  vertexHandleStyle,
  vertexHandleSelectedStyle,
} from "@/components/Map/mapStyles";
import TenantContext, { TenantContextType } from "@/context/TenantContext/TenantContext";
import MapContext, { MapContextType } from "@/context/MapContext/MapContext";

import GeoBaseLayerSwitcher from "@/components/Map/Controls/GEO/GeoBaseLayerSwitcher";
import GeoAPILayers from "@/components/Map/Layers/GEO/GeoAPILayers";
import VertexPopup from "@/views/GIS/VertexPopup";

// d.ts
import { ViewOptionsType } from "@/@types/common";

// Services
import mapService from "@/services/mapService";
import { IFieldWKT } from "@/@types/models/model";

type FeatureMapMode = "insert" | "view" | "update";

type FeatureMapProps = {
  mapId?: number;
  feature?: OlFeature;
  secondaryFeature?: OlFeature;
  field?: IFieldWKT;
  mode: FeatureMapMode;
  geometryChangeType?: "LineString" | "Polygon" | "Point";
  onGeometryChange?: (geom?: OlFeature) => void;
};

type VertexMeta = {
  kind: "Point" | "LineString" | "Polygon";
  coordIndex: number;
  ringIndex?: number;
};

type VertexFeature = OlFeature<Point>;

type VertexModifyInteractionProps = {
  source: OlSourceVector<OlFeature<Geometry>>;
  enabled: boolean;
  onModifyEnd?: () => void;
};

const getSegmentDistanceSquared = (point: [number, number], start: [number, number], end: [number, number]) => {
  const dx = end[0] - start[0];
  const dy = end[1] - start[1];

  if (dx === 0 && dy === 0) {
    const sx = point[0] - start[0];
    const sy = point[1] - start[1];
    return sx * sx + sy * sy;
  }

  const t = Math.max(0, Math.min(1, ((point[0] - start[0]) * dx + (point[1] - start[1]) * dy) / (dx * dx + dy * dy)));
  const projX = start[0] + t * dx;
  const projY = start[1] + t * dy;

  const px = point[0] - projX;
  const py = point[1] - projY;
  return px * px + py * py;
};

let projectionsRegistered = false;
const ensureProjectionsRegistered = () => {
  if (projectionsRegistered) {
    return;
  }

  proj4.defs(
    "EPSG:3765",
    "+proj=tmerc +lat_0=0 +lon_0=16.5 +k=0.9999 +x_0=500000 +y_0=0 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs"
  );
  proj4.defs(
    "EPSG:3857",
    "+proj=merc +a=6378137 +b=6378137 +lat_ts=0.0 +lon_0=0.0 +x_0=0.0 +y_0=0 +k=1.0 +units=m +nadgrids=@null +wktext  +no_defs"
  );
  OlRegister(proj4);
  projectionsRegistered = true;
};

const VertexModifyInteraction = ({ source, enabled, onModifyEnd }: VertexModifyInteractionProps) => {
  const mapContext = useContext(MapContext) as MapContextType;
  const interactionRef = useRef<Modify | null>(null);

  useEffect(() => {
    if (!mapContext?.map || !enabled) {
      return undefined;
    }

    const interaction = new Modify({ source, pixelTolerance: 12 });
    mapContext.map.addInteraction(interaction);
    interactionRef.current = interaction;
    const modifyEndKey = interaction.on("modifyend", () => {
      onModifyEnd?.();
    });

    return () => {
      unByKey(modifyEndKey);
      if (mapContext.map && interactionRef.current) {
        mapContext.map.removeInteraction(interactionRef.current);
      }
      interactionRef.current = null;
    };
  }, [mapContext?.map, source, enabled, onModifyEnd]);

  return null;
};


const FeatureMap = (props: FeatureMapProps) => {
  ensureProjectionsRegistered();

  const tenantContext = useContext(TenantContext) as TenantContextType;
  const { t } = useTranslation();

  const { feature, secondaryFeature, mapId, geometryChangeType, field } = props;
  const { mode, onGeometryChange } = props;

  const [zoomToExtent, setZoomToExtent] = useState<number[] | undefined>(undefined);
  const [mapInitialized, setMapInitialized] = useState(false);

  const [viewOptions, setViewOptions] = useState<ViewOptionsType>({
    center: tenantContext?.mapSettings?.initial_view_center ? tenantContext.mapSettings.initial_view_center : [1731757, 5581737],
    zoom: tenantContext?.mapSettings?.initial_view_zoom ? tenantContext.mapSettings.initial_view_zoom : 14,
    extent: tenantContext?.mapSettings?.max_extent ? tenantContext.mapSettings.max_extent : [1437016, 5271097, 2206278, 5860579],
    minZoom: 4,
    maxZoom: 25,
    constrainResolution: true,
  });

  const [layersCollection, setLayersCollection] = useState<OlCollection<OlBaseLayer> | undefined>(undefined);

  const [selectedSource] = useState<OlSourceVector<OlFeature<Geometry>>>(new OlSourceVector({}));
  const [secondarySelectedSource] = useState<OlSourceVector<OlFeature<Geometry>>>(new OlSourceVector({}));
  const [drawingSource] = useState<OlSourceVector<OlFeature<Geometry>>>(new OlSourceVector({}));
  const [vertexSource] = useState<OlSourceVector<OlFeature<Geometry>>>(new OlSourceVector({}));

  const [editableFeature, setEditableFeature] = useState<OlFeature<Geometry> | null>(null);
  const [selectedVertex, setSelectedVertex] = useState<VertexFeature | null>(null);
  const [vertexPopupPosition, setVertexPopupPosition] = useState<[number, number] | undefined>(undefined);
  const [coordX, setCoordX] = useState("");
  const [coordY, setCoordY] = useState("");
  const popupInitialCoordRef = useRef<{ x: string; y: string } | null>(null);

  const mapProjection = field?.targetProjection || "EPSG:3857";
  const editProjection = field?.sourceProjection || mapProjection;
  const srsCode = editProjection.includes(":") ? editProjection.split(":")[1] : editProjection;

  const showVertexEditing = (mode === "insert" || mode === "update") && !!editableFeature;
  const shouldDrawGeometry = (mode === "insert" || mode === "update") && !editableFeature;

  const vertexPointStyle = useMemo(() => vertexHandleStyle(), []);
  const vertexPointSelectedStyle = useMemo(() => vertexHandleSelectedStyle(), []);

  const toEditProjection = useCallback(
    (coord: [number, number]): [number, number] => {
      if (editProjection === mapProjection) {
        return coord;
      }
      return olTransform(coord, mapProjection, editProjection) as [number, number];
    },
    [editProjection, mapProjection]
  );

  const toMapProjection = useCallback(
    (coord: [number, number]): [number, number] => {
      if (editProjection === mapProjection) {
        return coord;
      }
      return olTransform(coord, editProjection, mapProjection) as [number, number];
    },
    [editProjection, mapProjection]
  );

  const clearVertexPopup = useCallback(() => {
    setSelectedVertex(null);
    setVertexPopupPosition(undefined);
    setCoordX("");
    setCoordY("");
  }, []);

  const buildVertexFeatures = useCallback(
    (targetFeature: OlFeature<Geometry> | null) => {
      vertexSource.clear();

      const geom = targetFeature?.getGeometry();
      if (!geom) {
        return;
      }

      const addVertex = (coord: [number, number], meta: VertexMeta) => {
        const vertexFeature = new OlFeature(new Point(coord));
        vertexFeature.set("vertexMeta", meta);
        vertexSource.addFeature(vertexFeature);
      };

      if (geom instanceof Point) {
        addVertex(geom.getCoordinates() as [number, number], { kind: "Point", coordIndex: 0 });
        return;
      }

      if (geom instanceof LineString) {
        geom.getCoordinates().forEach((coord, coordIndex) => {
          addVertex(coord as [number, number], { kind: "LineString", coordIndex });
        });
        return;
      }

      if (geom instanceof Polygon) {
        geom.getCoordinates().forEach((ring, ringIndex) => {
          const hasClosingCoord = ring.length > 1 && ring[0][0] === ring[ring.length - 1][0] && ring[0][1] === ring[ring.length - 1][1];
          const ringVertices = hasClosingCoord ? ring.slice(0, -1) : ring;

          ringVertices.forEach((coord, coordIndex) => {
            addVertex(coord as [number, number], { kind: "Polygon", ringIndex, coordIndex });
          });
        });
      }
    },
    [vertexSource]
  );

  const applyVertexToGeometry = useCallback(
    (vertexFeature: VertexFeature, emitChange = true) => {
      if (!editableFeature) {
        return;
      }

      const vertexGeom = vertexFeature.getGeometry();
      if (!(vertexGeom instanceof Point)) {
        return;
      }

      const meta = vertexFeature.get("vertexMeta") as VertexMeta | undefined;
      const mainGeom = editableFeature.getGeometry();
      if (!meta || !mainGeom) {
        return;
      }

      const nextCoord = vertexGeom.getCoordinates() as [number, number];

      if (mainGeom instanceof Point && meta.kind === "Point") {
        mainGeom.setCoordinates(nextCoord);
      } else if (mainGeom instanceof LineString && meta.kind === "LineString") {
        const next = mainGeom.getCoordinates().slice();
        next[meta.coordIndex] = nextCoord;
        mainGeom.setCoordinates(next);
      } else if (mainGeom instanceof Polygon && meta.kind === "Polygon" && typeof meta.ringIndex === "number") {
        const rings = mainGeom.getCoordinates().map((ring) => ring.slice());
        const ring = rings[meta.ringIndex]?.slice();

        if (!ring || ring.length === 0) {
          return;
        }

        ring[meta.coordIndex] = nextCoord;

        if (ring.length > 1) {
          if (meta.coordIndex === 0) {
            ring[ring.length - 1] = nextCoord;
          }
          if (meta.coordIndex === ring.length - 1) {
            ring[0] = nextCoord;
          }
        }

        rings[meta.ringIndex] = ring;
        mainGeom.setCoordinates(rings);
      }

      editableFeature.setGeometry(mainGeom);
      selectedSource.changed();

      if (selectedVertex === vertexFeature) {
        const current = vertexGeom.getCoordinates() as [number, number];
        setVertexPopupPosition(current);
        const transformed = toEditProjection(current);
        setCoordX(transformed[0].toFixed(2));
        setCoordY(transformed[1].toFixed(2));
      }

      if (emitChange && onGeometryChange) {
        onGeometryChange(editableFeature as unknown as OlFeature);
      }
    },
    [editableFeature, onGeometryChange, selectedSource, selectedVertex, toEditProjection]
  );

  const setFeatureOnMap = useCallback(
    (inputFeature?: OlFeature | null) => {
      selectedSource.clear();
      drawingSource.clear();
      clearVertexPopup();

      if (!inputFeature) {
        setEditableFeature(null);
        vertexSource.clear();
        return;
      }

      const clonedFeature = inputFeature.clone() as OlFeature<Geometry>;
      setEditableFeature(clonedFeature);
      selectedSource.addFeature(clonedFeature);
      buildVertexFeatures(clonedFeature);

      const geom = clonedFeature.getGeometry();
      if (geom) {
        const extent = geom.getExtent();
        const center = OlExtent.getCenter(extent) as [number, number];
        setViewOptions((prevState) => ({
          ...prevState,
          center,
          zoom: 18,
        }));
        setZoomToExtent(extent);
      }
    },
    [buildVertexFeatures, clearVertexPopup, drawingSource, selectedSource, vertexSource]
  );

  useEffect(() => {
    mapService.getLayers(mapId || 3).then((coll) => {
      setLayersCollection(coll);
      setMapInitialized(true);
    });
  }, [mapId]);

  useEffect(() => {
    setFeatureOnMap(feature || null);
  }, [feature, setFeatureOnMap]);

  useEffect(() => {
    secondarySelectedSource.clear();
    if (secondaryFeature) {
      secondarySelectedSource.addFeature(secondaryFeature as OlFeature<Geometry>);
    }
  }, [secondaryFeature, secondarySelectedSource]);

  useEffect(() => {
    if (!showVertexEditing) {
      return undefined;
    }

    const key = vertexSource.on("changefeature", (evt: any) => {
      const changedVertexFeature = evt.feature as VertexFeature | undefined;
      if (changedVertexFeature) {
        applyVertexToGeometry(changedVertexFeature, false);
      }
    });

    return () => {
      unByKey(key);
    };
  }, [showVertexEditing, vertexSource, applyVertexToGeometry]);

  const onDrawStart = useCallback(() => {
    drawingSource.clear();
    clearVertexPopup();
  }, [drawingSource, clearVertexPopup]);

  const onDrawEnd = useCallback(
    (evt: DrawEvent) => {
      if (!evt.feature) {
        return;
      }

      const nextFeature = evt.feature as OlFeature<Geometry>;
      setEditableFeature(nextFeature);

      selectedSource.clear();
      selectedSource.addFeature(nextFeature);
      buildVertexFeatures(nextFeature);

      const geom = nextFeature.getGeometry();
      if (geom) {
        setZoomToExtent(geom.getExtent());
      }

      if (onGeometryChange) {
        onGeometryChange(nextFeature as unknown as OlFeature);
      }
    },
    [buildVertexFeatures, onGeometryChange, selectedSource]
  );

  const onDrawChange = useCallback(() => {
    // keep for compatibility with DrawInteraction props
  }, []);

  const onVertexModifyEnd = useCallback(() => {
    if (onGeometryChange && editableFeature) {
      onGeometryChange(editableFeature as unknown as OlFeature);
    }
  }, [editableFeature, onGeometryChange]);

  const onMapClick = useCallback(
    (evt: any) => {
      if (!showVertexEditing || !evt?.map) {
        return;
      }

      const clickedVertexFeature = evt.map.forEachFeatureAtPixel(
        evt.pixel,
        (featureAtPixel: unknown, layer: OlBaseLayer) => {
          if (layer && layer.get("id") === "vertexPoints") {
            return featureAtPixel as VertexFeature;
          }
          return undefined;
        },
        { hitTolerance: 8 }
      ) as VertexFeature | undefined;

      if (!clickedVertexFeature) {
        clearVertexPopup();
        return;
      }

      const pointGeom = clickedVertexFeature.getGeometry();
      if (!(pointGeom instanceof Point)) {
        clearVertexPopup();
        return;
      }

      const mapCoord = pointGeom.getCoordinates() as [number, number];
      const transformed = toEditProjection(mapCoord);
      const nextX = transformed[0].toFixed(2);
      const nextY = transformed[1].toFixed(2);

      setSelectedVertex(clickedVertexFeature);
      setVertexPopupPosition(mapCoord);
      setCoordX(nextX);
      setCoordY(nextY);
      popupInitialCoordRef.current = { x: nextX, y: nextY };
    },
    [clearVertexPopup, showVertexEditing, toEditProjection]
  );

  const onMapDoubleClick = useCallback(
    (evt: any) => {
      if (!showVertexEditing || !editableFeature || !evt?.map?.forEachFeatureAtPixel) {
        return;
      }

      const clickedEditableFeature = evt.map.forEachFeatureAtPixel(
        evt.pixel,
        (featureAtPixel: unknown, layer: OlBaseLayer) => {
          if (layer && layer.get("id") === "selected") {
            return featureAtPixel as OlFeature<Geometry>;
          }
          return undefined;
        },
        { hitTolerance: 8 }
      ) as OlFeature<Geometry> | undefined;

      if (!clickedEditableFeature || clickedEditableFeature !== editableFeature) {
        return;
      }

      const geom = editableFeature.getGeometry();
      if (!geom) {
        return;
      }

      const clickCoord = evt.coordinate as [number, number];

      if (geom instanceof LineString) {
        const coords = geom.getCoordinates() as [number, number][];
        if (coords.length < 2) {
          return;
        }

        let minDistance = Number.POSITIVE_INFINITY;
        let insertIndex = 1;
        for (let i = 0; i < coords.length - 1; i += 1) {
          const dist = getSegmentDistanceSquared(clickCoord, coords[i], coords[i + 1]);
          if (dist < minDistance) {
            minDistance = dist;
            insertIndex = i + 1;
          }
        }

        const nextCoords = coords.slice();
        nextCoords.splice(insertIndex, 0, clickCoord);
        geom.setCoordinates(nextCoords);
      } else if (geom instanceof Polygon) {
        const rings = geom.getCoordinates().map((ring) => ring.slice()) as [number, number][][];

        let minDistance = Number.POSITIVE_INFINITY;
        let targetRingIndex = -1;
        let insertIndex = -1;

        rings.forEach((ring, ringIndex) => {
          if (ring.length < 2) {
            return;
          }

          for (let i = 0; i < ring.length - 1; i += 1) {
            const dist = getSegmentDistanceSquared(clickCoord, ring[i] as [number, number], ring[i + 1] as [number, number]);
            if (dist < minDistance) {
              minDistance = dist;
              targetRingIndex = ringIndex;
              insertIndex = i + 1;
            }
          }
        });

        if (targetRingIndex < 0 || insertIndex < 0) {
          return;
        }

        rings[targetRingIndex].splice(insertIndex, 0, clickCoord);
        geom.setCoordinates(rings);
      } else {
        return;
      }

      editableFeature.setGeometry(geom);
      selectedSource.changed();
      buildVertexFeatures(editableFeature);
      clearVertexPopup();

      if (evt.preventDefault) {
        evt.preventDefault();
      }
      if (evt.originalEvent?.preventDefault) {
        evt.originalEvent.preventDefault();
      }

      if (onGeometryChange) {
        onGeometryChange(editableFeature as unknown as OlFeature);
      }
    },
    [showVertexEditing, editableFeature, selectedSource, buildVertexFeatures, clearVertexPopup, onGeometryChange]
  );

  const onVertexPopupCommit = useCallback((closeAfterCommit = false) => {
    if (!selectedVertex) {
      return;
    }

    const x = Number(coordX);
    const y = Number(coordY);

    if (!Number.isFinite(x) || !Number.isFinite(y)) {
      return;
    }

    const mapCoord = toMapProjection([x, y]);
    const geom = selectedVertex.getGeometry();

    if (geom instanceof Point) {
      geom.setCoordinates(mapCoord);
      setVertexPopupPosition(mapCoord);
      applyVertexToGeometry(selectedVertex);
      popupInitialCoordRef.current = { x: coordX, y: coordY };
    }

    if (closeAfterCommit) {
      clearVertexPopup();
    }
  }, [selectedVertex, coordX, coordY, toMapProjection, applyVertexToGeometry, clearVertexPopup]);

  const onVertexPopupCancel = useCallback(() => {
    const initialCoords = popupInitialCoordRef.current;
    if (initialCoords) {
      setCoordX(initialCoords.x);
      setCoordY(initialCoords.y);
    }
    clearVertexPopup();
  }, [clearVertexPopup]);

  const canDeleteSelectedVertex = useMemo(() => {
    if (!selectedVertex || !editableFeature) {
      return false;
    }

    const mainGeom = editableFeature.getGeometry();
    const meta = selectedVertex.get("vertexMeta") as VertexMeta | undefined;

    if (!mainGeom || !meta) {
      return false;
    }

    if (mainGeom instanceof LineString && meta.kind === "LineString") {
      return mainGeom.getCoordinates().length > 2;
    }

    if (mainGeom instanceof Polygon && meta.kind === "Polygon" && typeof meta.ringIndex === "number") {
      const ring = mainGeom.getCoordinates()[meta.ringIndex];
      if (!ring || ring.length < 4) {
        return false;
      }
      const uniqueVertices = ring.slice(0, -1);
      return uniqueVertices.length > 3;
    }

    return false;
  }, [selectedVertex, editableFeature]);

  const onDeleteVertex = useCallback(() => {
    if (!selectedVertex || !editableFeature || !canDeleteSelectedVertex) {
      return;
    }

    const meta = selectedVertex.get("vertexMeta") as VertexMeta | undefined;
    const mainGeom = editableFeature.getGeometry();
    if (!meta || !mainGeom) {
      return;
    }

    if (mainGeom instanceof LineString && meta.kind === "LineString") {
      const coords = mainGeom.getCoordinates().slice() as [number, number][];
      if (meta.coordIndex < 0 || meta.coordIndex >= coords.length || coords.length <= 2) {
        return;
      }
      coords.splice(meta.coordIndex, 1);
      if (coords.length < 2) {
        return;
      }
      mainGeom.setCoordinates(coords);
    } else if (mainGeom instanceof Polygon && meta.kind === "Polygon" && typeof meta.ringIndex === "number") {
      const rings = mainGeom.getCoordinates().map((ring) => ring.slice()) as [number, number][][];
      const ring = rings[meta.ringIndex]?.slice();
      if (!ring || ring.length < 4) {
        return;
      }

      const uniqueVertices = ring.slice(0, -1);
      if (meta.coordIndex < 0 || meta.coordIndex >= uniqueVertices.length || uniqueVertices.length <= 3) {
        return;
      }

      uniqueVertices.splice(meta.coordIndex, 1);
      if (uniqueVertices.length < 3) {
        return;
      }

      rings[meta.ringIndex] = [...uniqueVertices, uniqueVertices[0]];
      mainGeom.setCoordinates(rings);
    } else {
      return;
    }

    editableFeature.setGeometry(mainGeom);
    selectedSource.changed();
    buildVertexFeatures(editableFeature);
    clearVertexPopup();

    if (onGeometryChange) {
      onGeometryChange(editableFeature as unknown as OlFeature);
    }
  }, [selectedVertex, editableFeature, canDeleteSelectedVertex, selectedSource, buildVertexFeatures, clearVertexPopup, onGeometryChange]);

  const onDeleteGeometry = useCallback(() => {
    selectedSource.clear();
    drawingSource.clear();
    vertexSource.clear();
    setEditableFeature(null);
    clearVertexPopup();

    if (onGeometryChange) {
      onGeometryChange(undefined);
    }
  }, [clearVertexPopup, drawingSource, onGeometryChange, selectedSource, vertexSource]);

  return tenantContext && layersCollection ? (
    <Map
      height="400px"
      view={viewOptions}
      className="feature-map"
      id="feature-map"
      initialized={mapInitialized}
      moveTolerance={10}
      maxTilesLoading={10}
      zoomToExtent={zoomToExtent}
      zoomToExtentPadding={[20, 20, 20, 20]}
      onClick={onMapClick}
      onDblclick={onMapDoubleClick}
    >
      <Controls>
        <ZoomControl zoomInTipLabel={t("map:controls.zoom_in") as string} zoomOutTipLabel={t("map:controls.zoom_out") as string} />
        <ScaleLineControl type="minimap" />
        <GeoBaseLayerSwitcher mapId={mapId || 3} allowNoLayer={false} />
        {(mode === "insert" || mode === "update") && !!editableFeature && (
          <ButtonControl
            id="feature-map-delete-geometry"
            title={t("buttons.delete") as string}
            onClick={onDeleteGeometry}
            sx={{ borderRadius: 0, bottom: 8, right: 8, position: "absolute" }}
          >
            <DeleteOutline fontSize="small" />
          </ButtonControl>
        )}
      </Controls>
      <Layers>
        {layersCollection ? (
          <Layers>
            <GeoAPILayers layersCollection={layersCollection} hiddenIDs={[]}/>
            <VectorLayer id="selected" source={selectedSource} style={selectedStrokeOnlyStyle} zIndex={1000} />
            <VectorLayer id="secondarySelected" source={secondarySelectedSource} style={highlightedVectorCustom} zIndex={1001} />
            {showVertexEditing && (
              <VectorLayer
                id="vertexPoints"
                source={vertexSource}
                zIndex={1010}
                style={(feat: unknown) => (feat === selectedVertex ? vertexPointSelectedStyle : vertexPointStyle)}
              />
            )}
          </Layers>
        ) : null}
      </Layers>
      <Overlays>
        {selectedVertex && vertexPopupPosition && (
          <VertexPopup
            position={vertexPopupPosition}
            coordX={coordX}
            coordY={coordY}
            srsCode={srsCode}
            onCoordXChange={setCoordX}
            onCoordYChange={setCoordY}
            onCommit={onVertexPopupCommit}
            onCancel={onVertexPopupCancel}
            onDeleteVertex={onDeleteVertex}
            canDeleteVertex={canDeleteSelectedVertex}
          />
        )}
      </Overlays>
      <Interactions>
        {mode === "view" || mode === "insert" || mode === "update" ? <DefaultInteractions /> : null}
        {shouldDrawGeometry ? (
          <DrawInteraction
            source={drawingSource}
            type={!geometryChangeType ? "Point" : geometryChangeType}
            style={measuringStyle}
            onChange={onDrawChange}
            onDrawstart={onDrawStart}
            onDrawend={onDrawEnd}
          />
        ) : null}
        {showVertexEditing ? <VertexModifyInteraction source={vertexSource} enabled onModifyEnd={onVertexModifyEnd} /> : null}
      </Interactions>
    </Map>
  ) : null;
};

export default FeatureMap;
